/**
 * COPY STRATEGY — TaxDeadlinesPage
 *
 * Awareness:      Problem-Aware (Stage 2/5)
 *                 Reader knows they must pay taxes but doesn't know exact dates.
 *                 Time-sensitive utility content — bookmarked and revisited.
 *
 * Sophistication: Stage 2 — Enlarged Claim
 *                 "Pavėluosite = prarasite" — consequence-enlarged claim.
 *                 Direct, practical, reference-style content. No mechanism needed.
 *
 * Avatar:         Any active IV driver, especially first-year.
 *                 Searching "kada moketi mokescius" or "mokesciu terminai 2026".
 *                 Needs a calendar they can trust and reference monthly.
 *
 * Hook:           Consequence — "Vienas pavėluotas mokėjimas = 10-50% bauda"
 * Structure:      Urgency alert → Monthly calendar → Penalty info → CTA
 * Proof:          Exact dates, penalty percentages, MMA changes for 2026
 * Funnel:         Utility content that builds trust, leads to Finhudas reminders
 */
import { motion } from 'framer-motion'
import { PiCalendarCheck, PiWarning } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const TaxDeadlinesPage = () => {
  usePageSetup('Mokesčių kalendorius 2026: pavėluosite = prarasite', {
    scroll: true,
    description: 'Visi pavežėjų mokesčių terminai 2026 m.: PVM iki 25 d., PSD iki mėnesio pabaigos, GPM311 iki gegužės 2 d. Vienas pavėluotas mokėjimas = 10–50% bauda. Kalendorius su priminimais.',
    type: 'article',
    datePublished: '2025-12-10',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Mokesčių kalendorius 2026: pavėluosite = prarasite
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Mokesčiai</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">6 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Vienas pavėluotas mokėjimas = 10-50% bauda. Štai datos, kurių NEGALITE praleisti.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-6">
          Šis mokesčių kalendorius apima visus svarbius terminus 2026 metams — PVM, PSD, GPM deklaracijas, naujas MMA normas ir baudų dydžius.
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl mb-4">
          <div className="flex items-start gap-3">
            <PiWarning className="min-w-6 h-6 text-blue-700 mt-1" />
            <div>
              <MotionText as="h3" className="text-blue-900 text-base font-medium mb-2">
                Svarbu 2026 m.!
              </MotionText>
              <MotionText as="p" className="text-blue-800 text-sm mb-2">
                <strong>Sausio 1 d.:</strong> Nauja MMA (Minimali mėnesinė alga) padidėjo iki 1 153 €
              </MotionText>
              <MotionText as="p" className="text-blue-700 text-sm">
                Tai reiškia, kad mėnesinis PSD išaugo nuo 72,45 € (2025 m.) iki 80,48 € (2026 m.)
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Mėnesiniai mokėjimai
        </MotionText>

        <MotionDiv className="space-y-4">
          <MotionDiv className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded-r-xl">
            <div className="flex items-start gap-3">
              <PiCalendarCheck className="min-w-6 h-6 text-blue-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-blue-900 text-base font-medium mb-1">
                  Iki 25-os kito mėnesio dienos
                </MotionText>
                <MotionText as="p" className="text-blue-800 text-sm mb-2">
                  Atvirkštinis PVM už komisinį mokestį
                </MotionText>
                <MotionText as="p" className="text-blue-700 text-xs">
                  Pvz.: už sausio mėnesį deklaruojama ir mokama iki vasario 25 d.
                </MotionText>
              </div>
            </div>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-green-500 bg-green-50 rounded-r-xl">
            <div className="flex items-start gap-3">
              <PiCalendarCheck className="min-w-6 h-6 text-green-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-green-900 text-base font-medium mb-1">
                  Iki einamojo mėnesio pabaigos
                </MotionText>
                <MotionText as="p" className="text-green-800 text-sm mb-2">
                  Minimalus PSD (6,98% nuo MMA)
                </MotionText>
                <MotionText as="p" className="text-green-700 text-xs mb-2">
                  <strong>2024 m.:</strong> 64,51 € (MMA 924 €)
                </MotionText>
                <MotionText as="p" className="text-green-700 text-xs mb-2">
                  <strong>2025 m.:</strong> 72,45 € (MMA 1 038 €)
                </MotionText>
                <MotionText as="p" className="text-green-700 text-xs mb-2">
                  <strong>2026 m.:</strong> 80,48 € (MMA 1 153 €)
                </MotionText>
                <MotionText as="p" className="text-green-700 text-xs">
                  Mokama už einamąjį mėnesį (pvz., už sausį mokama iki sausio 31 d.). Galite nemokėti mėnesius, kai nedirbate ir neturite pajamų.
                </MotionText>
              </div>
            </div>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Metiniai mokėjimai
        </MotionText>

        <MotionDiv className="space-y-4">
          <MotionDiv className="p-4 border-l-4 border-purple-500 bg-purple-50 rounded-r-xl">
            <div className="flex items-start gap-3">
              <PiCalendarCheck className="min-w-6 h-6 text-purple-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-purple-900 text-base font-medium mb-1">
                  Iki gegužės 2 dienos (2026 m.)
                </MotionText>
                <MotionText as="p" className="text-purple-800 text-sm mb-3">
                  Pateikti GPM311 deklaraciją per EDS sistemą ir sumokėti:
                </MotionText>
                <ul className="space-y-2">
                  <li className="text-purple-800 text-sm">
                    <strong>GPM</strong> - 5-20% nuo grynųjų pajamų (efektyvus tarifas: 5% iki 20k € pelno dėl mokesčio kredito, 20% virš 42,5k €)
                  </li>
                  <li className="text-purple-800 text-sm">
                    <strong>Likutinis PSD</strong> - jei metinės pajamos viršijo 12 MMA
                  </li>
                  <li className="text-purple-800 text-sm">
                    <strong>VSD</strong> - 12,52% (arba 15,52% su pensijos kaupimo 3%) nuo 90% apmokestinamųjų pajamų
                  </li>
                </ul>
                <MotionDiv className="mt-3 p-2 bg-purple-100 rounded">
                  <MotionText as="p" className="text-purple-900 text-xs">
                    ℹ️ 5% tarifas gaunamas per mokesčio kreditą: bazinis 20% tarifas - 15% kreditas = 5% efektyvus tarifas
                  </MotionText>
                </MotionDiv>
                <MotionText as="p" className="text-purple-700 text-xs mt-3">
                  <strong>Svarbu:</strong> Palaukite iki kovo vidurio, kol VMI įkels platformų duomenis (Bolt, Uber, Wolt)
                </MotionText>
              </div>
            </div>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mt-8">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Baudos už pavėlavimą
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  • Pirmą kartą pavėlavus - 10% baudos nuo sumos
                </li>
                <li className="text-amber-800 text-sm">
                  • Pasikartojus - iki 50% baudos
                </li>
                <li className="text-amber-800 text-sm">
                  • Delspinigiai - 0,03% už kiekvieną pavėluotą dieną
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Detalus 2026 m. mokesčių kalendorius
        </MotionText>

        <MotionDiv className="space-y-3">
          <MotionDiv className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
            <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-1">
              Sausis 2026
            </MotionText>
            <MotionText as="p" className="text-amber-800 text-sm mb-1">
              <strong>Sau. 1:</strong> Nauja MMA įsigalioja (1 153 €) - PSD pakyla iki 80,48 €
            </MotionText>
            <MotionText as="p" className="text-amber-800 text-sm">
              <strong>Sau. 31:</strong> PSD už gruodžio 2025 m. (72,45 €)
            </MotionText>
          </MotionDiv>

          <MotionDiv className="py-4 bg-zinc-50 rounded-xl">
            <MotionText as="h3" className="text-zinc-900 text-sm font-medium mb-2">
              Vasaris-Kovas 2026
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm mb-1">
              <strong>Kas mėnesį iki 25 d.:</strong> Atvirkštinis PVM už praėjusį mėnesį
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm mb-1">
              <strong>Kas mėnesį iki mėnesio pabaigos:</strong> PSD 72,45 € (2025 m.) / 80,48 € (2026 m.)
            </MotionText>
            <MotionText as="p" className="text-blue-700 text-xs mt-2">
              💡 Peržiūrėkite 2024 m. įrašus - įsitikinkite, kad turite visus Bolt/Uber/Wolt dokumentus
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl">
            <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
              Gegužės 2, 2026 - DIDŽIOJI METINĖ DEKLARACIJA
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-sm mb-2">
              <strong>1 veiksmas:</strong> Pateikti GPM311 deklaraciją per EDS
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-sm mb-2">
              <strong>2 veiksmas:</strong> Sumokėti GPM (5-20% efektyvus tarifas su mokesčio kreditu) į VMI (gavėjas 1882, kodas 1441)
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-sm mb-2">
              <strong>3 veiksmas:</strong> Sumokėti VSD (12,52%) į Sodrą (gavėjas 252, kodas 444)
            </MotionText>
            <MotionText as="p" className="text-blue-800 text-sm">
              <strong>4 veiksmas:</strong> Sumokėti likutinį PSD (jei yra)
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 bg-green-50 rounded-xl">
            <MotionText as="h3" className="text-green-900 text-sm font-medium mb-1">
              Liepa 2026
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm">
              <strong>Lie. 1:</strong> PVM slenksčio patikra - peržiūrėkite paskutinių 12 mėn. pajamas. Jei viršijote 45 000 €, privalote registruotis kaip PVM mokėtojas.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Specialios įspėjamos pavežėjams
        </MotionText>

        <MotionDiv className="space-y-4 mb-8">
          <MotionDiv className="p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
            <div className="flex items-start gap-3">
              <PiWarning className="min-w-6 h-6 text-red-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-red-900 text-sm font-medium mb-2">
                  45 000 € PVM spąstai
                </MotionText>
                <MotionText as="p" className="text-red-800 text-sm">
                  Jei bendros pajamos (prieš Bolt/Uber mokesčius) per bet kuriuos 12 mėnesių viršija 45 000 €, privalote registruotis kaip PVM mokėtojas. Tai labai apsunkina apskaitą!
                </MotionText>
              </div>
            </div>
          </MotionDiv>

          <MotionDiv className="p-4 bg-amber-50 border border-amber-300 rounded-xl">
            <div className="flex items-start gap-3">
              <PiWarning className="min-w-6 h-6 text-amber-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                  DAC7 duomenų dalijimasis
                </MotionText>
                <MotionText as="p" className="text-amber-800 text-sm">
                  Nuo 2023 m. platformos (Bolt, Uber, Wolt) privalo pranešti VMI, kiek uždirbote. Jei deklaruosite 10 000 €, o Bolt praneš 12 000 € - auditas garantuotas!
                </MotionText>
              </div>
            </div>
          </MotionDiv>

          <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <PiCalendarCheck className="min-w-6 h-6 text-blue-700 mt-1" />
              <div>
                <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
                  Atvirkštinis PVM (Reverse Charge)
                </MotionText>
                <MotionText as="p" className="text-blue-800 text-sm">
                  Net jei nesate PVM mokėtojas, perkant paslaugas iš ES įmonių (Bolt, Uber komisiniai), privalote mokėti "Reverse Charge VAT" už jų komisinį mokestį.
                </MotionText>
              </div>
            </div>
          </MotionDiv>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' },
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM už Bolt komisinius' },
          { slug: 'pvm-pakeitimai-pavezejams-2025', title: 'PVM pakeitimai nuo gegužės 1 d.' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default TaxDeadlinesPage
